import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Icon, Alert, Input } from '@polichat/flamboyant';
import { useGlotaT } from 'src/hooks/useGlotaTranslation';

import PageMainHeader from '../../../common/pages/PageMainHeader/PageMainHeader.component';
import PageMainBody from '../../../common/pages/PageMainBody/PageMainBody.component';

import {
  EditContainer,
  EditCard,
  EditHeader,
  EditContent,
  EditSection,
  EditField,
  EditLabel,
  EditInput,
  EditToggle,
  EditInfo,
  ActionButtons,
  StatusIndicator,
  ConnectionStatus,
  ProviderInfo,
  DeleteButton,
} from './ChannelAddEditWhatsappCoexistence.styles';

const ChannelAddEditWhatsappCoexistence = ({
  data,
  dataset,
  userAdmPoliGetChannelFromCustomerId,
}) => {
  const history = useHistory();
  const currentCustomer = useSelector(
    (state) => state?.general?.current_customer_id
  );

  const [formData, setFormData] = useState({
    channelName: data?.name || '',
    displayOnWebchat: data?.display_on_webchat || false,
    isActive: data?.status === 1,
  });

  const deleteChannelText = useGlotaT('spa.tools.deleteChannel');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [providerData, setProviderData] = useState(null);

  useEffect(() => {
    if (data?.config) {
      const config =
        typeof data.config === 'string' ? JSON.parse(data.config) : data.config;
      console.log('config.business_id: ', config.business_id);
      if (config.coexistence) {
        setProviderData({
          businessId: config.business_id,
          phoneNumberId: config.phone_number_id,
          providerId: config.provider_id,
        });
        setConnectionStatus(config.connection_status || 'disconnected');
      }
    }
  }, [data]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/customers/${currentCustomer}/channels/${data.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.channelName,
            display_on_webchat: formData.displayOnWebchat,
            status: formData.isActive ? 1 : 0,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao salvar canal');
      }

      const result = await response.json();
      dataset(result);

      // Show success message or redirect
      history.push('/tools/channels/list');
    } catch (err) {
      setError('Erro ao salvar configurações. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar este canal?')) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/customers/${currentCustomer}/channels/${data.id}/disconnect`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao desconectar canal');
      }

      setConnectionStatus('disconnected');
    } catch (err) {
      setError('Erro ao desconectar canal. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        'Tem certeza que deseja excluir este canal? Esta ação não pode ser desfeita.'
      )
    ) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/customers/${currentCustomer}/channels/${data.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao excluir canal');
      }

      history.push('/tools/channels/list');
    } catch (err) {
      setError('Erro ao excluir canal. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderConnectionStatus = () => {
    if (connectionStatus === 'connected') {
      return (
        <ConnectionStatus>
          <Icon icon="poli-icon pi-check-fill" size={16} color="#00C48C" />
          <span>Conectado ao WhatsApp</span>
        </ConnectionStatus>
      );
    }

    return (
      <ConnectionStatus>
        <Icon icon="poli-icon pi-close-fill" size={16} color="#dc3545" />
        <span>Desconectado do WhatsApp</span>
      </ConnectionStatus>
    );
  };

  return (
    <>
      <PageMainHeader
        title="Editar canal"
        icon="poli-icon pi-whatsapp-business-fill"
        iconSize="30"
      />

      <PageMainBody>
        <EditContainer>
          <EditCard>
            <EditHeader>
              <Icon
                icon="poli-icon pi-whatsapp-business-fill"
                size={40}
                color="#00C48C"
              />
              <h3>WhatsApp Business API</h3>
              <p>
                Essa é a integração utilizada com o Meta para conectar um número
                já usado no WhatsApp Business App à API em nuvem. Com isso, você
                poderá utilizar os serviços oferecidos pelo Grupo Meta, sem
                precisar perder seu número existente.
              </p>
              <DeleteButton onClick={handleDelete} disabled={isLoading}>
                <Icon icon="poli-icon pi-delete-fill" size={16} />
                {deleteChannelText}
              </DeleteButton>
            </EditHeader>

            <EditContent>
              <EditSection>
                <h4>Informações do Canal</h4>

                <EditField>
                  <EditLabel>Nome do canal</EditLabel>
                  <EditInput>
                    <Input
                      type="text"
                      value={formData.channelName}
                      onChange={(e) =>
                        handleInputChange('channelName', e.target.value)
                      }
                      placeholder="Ex: WhatsApp Principal"
                    />
                  </EditInput>
                </EditField>

                <EditField>
                  <EditLabel>Telefone</EditLabel>
                  <EditInput>
                    <Input
                      type="text"
                      value={data?.phone || ''}
                      disabled
                      style={{ backgroundColor: '#f8f9fa', color: '#666' }}
                    />
                    <EditInfo>
                      Este campo não pode ser editado após a conexão
                    </EditInfo>
                  </EditInput>
                </EditField>
              </EditSection>

              <EditSection>
                <h4>Dados do Provedor</h4>

                {providerData && (
                  <>
                    <EditField>
                      <EditLabel>ID do Provedor</EditLabel>
                      <EditInput>
                        <Input
                          type="text"
                          value={providerData.providerId || ''}
                          disabled
                          style={{ backgroundColor: '#f8f9fa', color: '#666' }}
                        />
                      </EditInput>
                    </EditField>

                    <EditField>
                      <EditLabel>Status da Conexão</EditLabel>
                      <StatusIndicator>
                        <Icon
                          icon="poli-icon pi-check-fill"
                          size={16}
                          color="#00C48C"
                        />
                        <span>Conectado ao provedor</span>
                      </StatusIndicator>
                    </EditField>
                  </>
                )}
              </EditSection>

              <EditSection>
                <h4>Permissões do Canal</h4>

                <EditToggle>
                  <Input
                    type="checkbox"
                    checked={formData.displayOnWebchat}
                    onChange={(e) =>
                      handleInputChange('displayOnWebchat', e.target.checked)
                    }
                  />
                  <span>Exibir canal no Widget do webchat</span>
                </EditToggle>

                <EditToggle>
                  <Input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      handleInputChange('isActive', e.target.checked)
                    }
                  />
                  <span>Status ativo</span>
                </EditToggle>
              </EditSection>

              <EditSection>
                <h4>Status da Conexão WhatsApp</h4>
                {renderConnectionStatus()}

                {connectionStatus === 'connected' && (
                  <Button
                    color="secondary"
                    onClick={handleDisconnect}
                    disabled={isLoading}
                    style={{ marginTop: '1rem' }}
                  >
                    <Icon icon="poli-icon pi-disconnect-line" size={16} />
                    Desconectar
                  </Button>
                )}
              </EditSection>

              {error && (
                <Alert color="danger">
                  <Icon icon="poli-icon pi-sad-line" size={20} />
                  <span>{error}</span>
                </Alert>
              )}

              <ActionButtons>
                <Button
                  color="secondary"
                  onClick={() => history.push('/tools/channels/list')}
                  disabled={isLoading}
                >
                  <Icon icon="poli-icon pi-arrow-left-line" size={16} />
                  Cancelar
                </Button>

                <Button
                  color="primary"
                  onClick={handleSave}
                  disabled={isLoading || !formData.channelName.trim()}
                >
                  {isLoading ? (
                    <>
                      <Icon icon="poli-icon pi-loading-fill" size={16} />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Icon icon="poli-icon pi-check-fill" size={16} />
                      Salvar
                    </>
                  )}
                </Button>
              </ActionButtons>
            </EditContent>
          </EditCard>
        </EditContainer>
      </PageMainBody>
    </>
  );
};

export default ChannelAddEditWhatsappCoexistence;
